import Head from "next/head"

// ---------------------------------------------------------

import { useContentfulLiveUpdates } from "@contentful/live-preview/react"
import { getBlogPostData, getRecentBlogPaths } from "@lib/api"
import { fetchRecommended } from "@lib/fetchRecommended"
import { generateArticleSchema } from "@lib/utils"

// ---------------------------------------------------------

import BlogTemplate from "@templates/blog"
import Layout from "@layout/index"

// ---------------------------------------------------------

export async function getStaticPaths() {
  const slugs = (await getRecentBlogPaths(90)) ?? []

  return {
    paths: slugs,
    fallback: "blocking"
  }
}

export async function getStaticProps(context) {
  const preview = process.env.NODE_ENV === "development" || context.draftMode || false
  const previewEnv = context?.previewData?.environment || null
  const data = (await getBlogPostData(context.params.slug, preview, previewEnv)) ?? []
  const schema = generateArticleSchema(data)
  const tags = data.tagCollection?.tags?.map((tag) => {
    return tag.id
  })

  if (!data.slug) {
    return {
      notFound: true,
      props: {}
    }
  }

  const recommended = data.showRecommended
    ? await fetchRecommended(
        data?.recommendedCollection?.items,
        context.params.slug,
        undefined,
        tags,
        preview
      )
    : null

  return {
    props: { preview, data, recommended, schema },
    revalidate: 300
  }
}

// ---------------------------------------------------------

const BlogPost = ({ data, preview, recommended, schema }) => {
  const liveData = useContentfulLiveUpdates(data)

  const meta = {
    robots: liveData?.isPrivate ? "noindex" : "index"
  }

  return (
    <Layout
      preview={preview}
      pageTitle={liveData.title}
      featuredImage={liveData.featuredImage}
      excerpt={liveData.excerpt}
      customMeta={meta}
    >
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schema }} />
      </Head>
      <BlogTemplate data={liveData} parent="blog" preview={preview} recommended={recommended} />
    </Layout>
  )
}

export default BlogPost
